import React, { useCallback, useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import fileTextSrc from '../assets/icons/FileText.svg'
import pdfIconSrc from '../assets/icons/file-icons/pdf-icon.svg'
import wordIconSrc from '../assets/icons/file-icons/word-icon.svg'
import excelIconSrc from '../assets/icons/file-icons/excel-icon.svg'
import pptIconSrc from '../assets/icons/file-icons/pptx-icon.svg'
import { MediaResponseDto, MediaTypeEnum } from '../new-api/api'
import { BASE_URL } from '../utils/shared'

function getDocIcon(ext: string): string {
  switch (ext) {
    case 'pdf':
      return pdfIconSrc
    case 'doc':
    case 'docx':
      return wordIconSrc
    case 'xls':
    case 'xlsx':
      return excelIconSrc
    case 'ppt':
    case 'pptx':
      return pptIconSrc
    default:
      return fileTextSrc
  }
}

function mediaUrl(url: string) {
  if (url?.startsWith('http')) return url
  return `${BASE_URL}/files/${url}`
}

function normalizeExt(ext?: string) {
  return ext?.toLowerCase().replace(/^\./, '') ?? ''
}

function getDocBg(ext: string): string {
  if (ext === 'pdf') return '#FEF2F2'
  if (['xls', 'xlsx'].includes(ext)) return '#F0FDF4'
  if (['doc', 'docx'].includes(ext)) return '#EFF6FF'
  if (['ppt', 'pptx'].includes(ext)) return '#B6462729'
  return '#F5F5F5'
}

function isVideo(m: MediaResponseDto) {
  return m.type === MediaTypeEnum.VIDEO
}

function isPdf(m: MediaResponseDto) {
  return m.mime === 'application/pdf' || m.extension?.toLowerCase().replace(/^\./, '') === 'pdf'
}

function isDocument(m: MediaResponseDto) {
  return m.type === MediaTypeEnum.DOCUMENT
}

interface TaskMediaModalProps {
  medias: MediaResponseDto[]
  initialIndex?: number
  onClose: () => void
}

export const TaskMediaModal: React.FC<TaskMediaModalProps> = ({ medias, initialIndex = 0, onClose }) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex)
  const [pdfBlobUrl, setPdfBlobUrl] = useState<string | null>(null)
  const media = medias[currentIndex]
  const thumbListRef = useRef<HTMLDivElement>(null)

  const prev = useCallback(() => setCurrentIndex((i) => Math.max(0, i - 1)), [])
  const next = useCallback(() => setCurrentIndex((i) => Math.min(medias.length - 1, i + 1)), [medias.length])

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') prev()
      if (e.key === 'ArrowRight') next()
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [prev, next, onClose])

  const url = mediaUrl(media.url)

  useEffect(() => {
    if (!isDocument(media) || !isPdf(media)) {
      setPdfBlobUrl(null)
      return
    }
    let objectUrl: string
    fetch(url, { credentials: 'include' })
      .then((r) => r.blob())
      .then((blob) => {
        objectUrl = URL.createObjectURL(blob)
        setPdfBlobUrl(objectUrl)
      })
      .catch(() => setPdfBlobUrl(null))
    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl)
      setPdfBlobUrl(null)
    }
  }, [url, media])

  return (
    <Overlay onClick={onClose}>
      <ModalWrapper onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <ModalHeader>
          <HeaderLeft>
            <FileName>{media.name}</FileName>
          </HeaderLeft>
          <HeaderCenter>
            <Counter>
              {currentIndex + 1} / {medias.length}
            </Counter>
          </HeaderCenter>
          <HeaderRight>
            <CloseBtn onClick={onClose} aria-label="Close">
              ✕
            </CloseBtn>
          </HeaderRight>
        </ModalHeader>

        {/* Main content */}
        <MainArea>
          {currentIndex > 0 && (
            <NavBtn side="left" onClick={prev}>
              ‹
            </NavBtn>
          )}
          <MediaContent>
            {isVideo(media) && (
              <video
                key={url}
                src={url}
                controls
                autoPlay
                style={{ maxWidth: '100%', maxHeight: '100%', borderRadius: 8 }}
                crossOrigin="use-credentials"
              />
            )}
            {!isVideo(media) && !isDocument(media) && (
              <img
                src={url}
                alt={media.name}
                style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', borderRadius: 8 }}
                crossOrigin="use-credentials"
              />
            )}
            {isDocument(media) &&
              isPdf(media) &&
              (pdfBlobUrl ? (
                <iframe
                  key={pdfBlobUrl}
                  src={pdfBlobUrl}
                  title={media.name}
                  style={{ width: '100%', height: '700px', border: 'none', borderRadius: 8 }}
                />
              ) : (
                <PdfLoading>Loading PDF…</PdfLoading>
              ))}
            {isDocument(media) && !isPdf(media) && (
              <DocFallback style={{ backgroundColor: getDocBg(normalizeExt(media.extension)) }}>
                <img
                  src={getDocIcon(normalizeExt(media.extension))}
                  alt={normalizeExt(media.extension)}
                  style={{ width: 64, height: 64, opacity: 0.85 }}
                />
                <DocName>{media.name}</DocName>
                <DocExt>{normalizeExt(media.extension).toUpperCase()}</DocExt>
                <DownloadBtn href={url} download={media.name} target="_blank" rel="noopener noreferrer">
                  Download
                </DownloadBtn>
              </DocFallback>
            )}
          </MediaContent>
          {currentIndex < medias.length - 1 && (
            <NavBtn side="right" onClick={next}>
              ›
            </NavBtn>
          )}
        </MainArea>

        {/* Thumbnail strip */}
        {medias.length > 1 && (
          <ThumbStrip ref={thumbListRef}>
            {medias.map((m, idx) => {
              const tUrl = mediaUrl(m.url)
              const selected = idx === currentIndex
              return (
                <ThumbItem key={idx} selected={selected} onClick={() => setCurrentIndex(idx)}>
                  {isDocument(m) ? (
                    <ThumbDoc style={{ backgroundColor: getDocBg(normalizeExt(m.extension)) }}>
                      <img
                        src={getDocIcon(normalizeExt(m.extension))}
                        alt={normalizeExt(m.extension)}
                        style={{ width: 24, height: 24, opacity: 0.85 }}
                      />
                      <ThumbDocExt>{normalizeExt(m.extension).toUpperCase()}</ThumbDocExt>
                    </ThumbDoc>
                  ) : isVideo(m) ? (
                    <ThumbImgWrapper>
                      <video
                        src={tUrl}
                        muted
                        preload="metadata"
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        crossOrigin="use-credentials"
                      />
                      <PlayOverlay>▶</PlayOverlay>
                    </ThumbImgWrapper>
                  ) : (
                    <ThumbImg src={tUrl} alt={m.name} crossOrigin="use-credentials" />
                  )}
                </ThumbItem>
              )
            })}
          </ThumbStrip>
        )}
      </ModalWrapper>
    </Overlay>
  )
}

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.85);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
`

const ModalWrapper = styled.div`
  background: #141414;
  border-radius: 12px;
  width: min(900px, 95vw);
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid #2a2a2a;
  flex-shrink: 0;
`

const HeaderLeft = styled.div`
  flex: 1;
`

const HeaderCenter = styled.div`
  flex-shrink: 0;
`

const HeaderRight = styled.div`
  flex: 1;
  display: flex;
  justify-content: flex-end;
`

const FileName = styled.span`
  font-size: 13px;
  color: #aaa;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 240px;
  display: block;
`

const Counter = styled.span`
  font-size: 14px;
  color: #fff;
  font-weight: 500;
`

const CloseBtn = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: #aaa;
  font-size: 20px;
  line-height: 1;
  padding: 4px 8px;
  border-radius: 4px;
  &:hover {
    color: #fff;
    background: #2a2a2a;
  }
`

const MainArea = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  position: relative;
  height: calc(90vh - 180px);
  overflow: hidden;
`

const MediaContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  min-height: 700px;
`

const NavBtn = styled.button<{ side: 'left' | 'right' }>`
  position: absolute;
  ${({ side }) => side}: 12px;
  top: 50%;
  transform: translateY(-50%);
  background: rgba(255, 255, 255, 0.1);
  border: none;
  border-radius: 50%;
  width: 44px;
  height: 44px;
  cursor: pointer;
  color: #fff;
  font-size: 28px;
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1;
  transition: background 0.15s;
  &:hover {
    background: rgba(255, 255, 255, 0.2);
  }
`

const ThumbStrip = styled.div`
  display: flex;
  gap: 8px;
  padding: 12px 20px;
  overflow-x: auto;
  border-top: 1px solid #2a2a2a;
  flex-shrink: 0;
  &::-webkit-scrollbar {
    height: 4px;
  }
  &::-webkit-scrollbar-thumb {
    background: #444;
    border-radius: 2px;
  }
`

const ThumbItem = styled.div<{ selected: boolean }>`
  width: 60px;
  height: 60px;
  border-radius: 6px;
  overflow: hidden;
  flex-shrink: 0;
  cursor: pointer;
  border: 2px solid ${({ selected }) => (selected ? '#59EEB9' : 'transparent')};
  transition: border-color 0.15s;
`

const ThumbImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`

const ThumbImgWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
`

const PlayOverlay = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.35);
  color: #fff;
  font-size: 14px;
`

const ThumbDoc = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 3px;
`

const ThumbDocExt = styled.span`
  font-size: 9px;
  font-weight: 700;
  color: #555;
  text-transform: uppercase;
  line-height: 1;
`

const DocFallback = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding: 48px 40px;
  border-radius: 12px;
`

const DocName = styled.div`
  color: #fff;
  font-size: 18px;
  text-align: center;
  max-width: 400px;
`

const DocExt = styled.div`
  color: #888;
  font-size: 13px;
  text-transform: uppercase;
`

const PdfLoading = styled.div`
  color: #aaa;
  font-size: 14px;
`

const DownloadBtn = styled.a`
  background: #59eeb9;
  color: #000;
  padding: 12px 24px;
  border-radius: 8px;
  text-decoration: none;
  font-weight: 600;
  font-size: 14px;
  &:hover {
    background: #3dd6a3;
  }
`
